import {FontAwesomeIcon as Icon} from '@fortawesome/react-fontawesome'
import AlertDanger from 'components/Alerts/AlertDanger'
import {Panel} from 'components/Panel'
import {User} from 'domain-models'
import {hasFlag} from 'filters'
import Form from 'mixins/form'
import {FormType} from 'mixins/FormTypes'
import {Http} from 'mixins/http'
import React, {createRef} from 'react'
import {connect} from 'react-redux'
import {NavLink, Redirect} from 'react-router-dom'
import {updateApp, updateUser} from 'redux/actions'
import {AppState} from 'redux/reducers/AppReducer'
import {joinChannels} from 'services/websockets/channels'
import {GlobalState} from 'state'
import {store} from 'store'
import Trans from '../../components/Trans'

type LoginProps = {
  app: AppState
  user: User
}

type LoginState = {
  redirect: boolean
  form: FormType
}

class Login extends React.Component<LoginProps, LoginState> {
  private userNameRef = createRef<HTMLInputElement>()
  private passwordRef = createRef<HTMLInputElement>()
  private _mounted = false

  constructor(props: LoginProps) {
    super(props)
    this.state = {
      redirect: false,
      form: new Form({username: null, password: null})
    }
  }

  componentDidMount() {
    this._mounted = true
  }

  componentWillUnmount() {
    this._mounted = false
  }

  login = async (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const form = new Form({
      username: this.userNameRef.current ? this.userNameRef.current.value : null,
      password: this.passwordRef.current ? this.passwordRef.current.value : null
    })
    if (this._mounted) {
      await this.setState({
        redirect: false,
        form: form
      })
    }

    const user = await Http.post<User>('/login', {state: this, form: this.state.form})
    const app = await window.axios.get('/reload')

    // CRITICAL: Set window.app BEFORE dispatching to Redux
    // Redux dispatch triggers Router re-render, which checks window.app.user
    if (app.data) {
      window.app = app.data
    }

    if (app.data && app.data.auctionV2 && app.data.auctionV2.redirect) {
      window.location.replace(app.data.auctionV2.url)
      return
    }

    await store.dispatch(updateUser(user))
    if (app.data) {
      await store.dispatch(updateApp(app.data))
    }
    joinChannels(user)
    if (this._mounted) {
      this.setState({
        redirect: true
      })
    }
  }

  render() {
    if (this.state.redirect) {
      try {
        // Use window.app directly to avoid stale Redux props
        const agbAccepted = hasFlag(window.app.user.employee, 'MetaFlags', 2)
        const agbConfigured = window.app.settings?.agbUrl || window.app.settings?.agbUrls
        const hasSpeedAuktionPermission = window.app.user?.authority?.mod_speed_auction_claim
        // Skip AGB page if already accepted, no AGB configured, or user has Speed-Auktion claim permission
        return <Redirect to={(agbAccepted || !agbConfigured || hasSpeedAuktionPermission) ? '/' : '/accept-agb'}/>
      } catch (error) {
        console.error('Login redirect error:', error);
        return <Redirect to='/accept-agb'/>
      }
    }

    return (
      <div className="border-t-4 border-blue-800">
        <div className="flex flex-col p-2 w-full max-w-2xl lg:max-w-3xl mx-auto">
          <Panel>
            <div className="flex flex-col items-center justify-center px-4 py-2">
              <img src={this.props.app.basename + '/images/undraw_login.png'} alt="login" className="w-32 h-32"/>
              <div className="flex flex-col sm:flex-row flex-wrap w-full">
                <div className="text-gray-600 font-semibold tracking-wide whitespace-no-wrap w-full lg:w-1/3 text-center sm:text-left">
                  <Icon icon="sign-in-alt"/>
                  <span> <Trans>Login</Trans></span>
                </div>
                <form className="w-full lg:w-2/3 flex flex-col">
                  {
                    this.state.form.errors.has('auth.failed') ?
                      <AlertDanger><Trans>Diese Benutzerdaten sind uns nicht bekannt</Trans></AlertDanger>
                      : null
                  }
                  <div>
                    <label className="block tracking-wide text-gray-600 font-semibold mb-1"><Trans>Benutzername</Trans></label>
                    <input name="username" ref={this.userNameRef} type="text" placeholder="Benutzername" className="shadow appearance-none block w-full bg-gray-200 text-gray-800 border border-gray-200 rounded py-2 px-3 leading-tight hover:bg-gray-100 focus:bg-gray-100 focus:border-gray-300"/>

                    {
                      this.state.form.errors.has('username') ?
                        <AlertDanger><Trans>Benutzername muss ausgefüllt sein.</Trans></AlertDanger>
                        : null
                    }
                  </div>

                  <div className='mt-4'>
                    <label className="block tracking-wide text-gray-600 font-semibold mb-1"><Trans>Passwort</Trans></label>
                    <input name="password" ref={this.passwordRef} type="password" placeholder="***************" className="shadow appearance-none block w-full bg-gray-200 text-gray-800 border border-gray-200 rounded py-2 px-3 leading-tight hover:bg-gray-100 focus:bg-gray-100 focus:border-gray-300"/>

                    {
                      this.state.form.errors.has('password') ?
                        <AlertDanger><Trans>Passwort muss ausgefüllt sein.</Trans></AlertDanger>
                        : null
                    }
                  </div>

                  <div className="mt-4">
                    <div className="mt-4 flex justify-end">
                      <NavLink to='/request-password-reset' className="text-gray-600 hover:text-blue-700 hover:font-semibold flex items-center mr-2 text-xs">
                        <Trans>Passwort vergessen?</Trans>
                      </NavLink>

                      <button className="rounded bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 font-semibold shadow" onClick={this.login}>
                        {this.state.form.busy ?
                          <Icon icon="spinner" spin={true}/>
                          :
                          <Icon icon="sign-in-alt"/>
                        }
                        <span> <Trans>Login</Trans></span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </Panel>
          <div className="flex flex-col items-center justify-center text-gray-800 font-semibold">
            <span>
              <Trans>Für eine korrekte Darstellung empfehlen wir die Verwendung von</Trans> <a href="https://www.mozilla.org/" target="_blank" className="text-blue-700 hover:text-blue-600"> Firefox </a> <Trans>oder</Trans> <a href="https://www.google.de/intl/de/chrome/" target="_blank" className="text-blue-700 hover:text-blue-600"> Chrome </a>
            </span>
            <span>
              <Trans>Der Internet Explorer wird derzeit nicht unterstützt.</Trans>
            </span>
          </div>
        </div>
      </div>
    )
  }
}

export default connect((state: GlobalState) => ({
  user: state.user,
  app: state.app
}))(Login)
